import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
} from "@mui/material";
import ActionSelect from "../ActionSelect";
import {
  Budget,
  BudgetItemType,
  BudgetStatusEnum,
  itemOptions,
  StatusBudget,
  Totals,
  getBudgetCurrentStatus,
} from "../../../../../types/budget/budget";
import { useCallback, useMemo, useState } from "react";
import { useBudget } from "../../../../../contexts/BudgetContext/projectbudgets.context";
import { formatCurrency } from "../../../../../utils/formatters";
import { BiChevronDown } from "react-icons/bi";
import { ItemTotalsSummary } from "../../BudgetItem/ItemTotalsSummary";
import BudgetItem from "../BudgetDetail/BudgetItem";
import { RadioGroup } from "../../../../UI/RadioGroup";
import { useProject } from "../../../../../contexts/project.context";
import { TabActive } from "../../../../../types/project.type";
import { hasConsultingBefore } from "../../ActiveBudget/ActiveBudget";
import { useBudgetSummaryActions } from "../../hooks/useBudgetSummaryActions";
import { BudgetSummary as BudgetSummaryDto } from "../../../../../types/budget/budget-summary";
import {
  budgetForRow,
  isFullBudget,
} from "../../../../../utils/budget-adapters";
import { featureFlags } from "../../../../../config/featureFlags";
import {
  getCollapsedBudgetSavings,
  getCollapsedBudgetTotal,
  resolveSidebarStageStatus,
} from "./collapsed-totals";
import { filterServicesCountedInStageTotals } from "../../../../../utils/budget-stage-visibility";
import { ServiceData } from "../../../../../types/budget/service-item";

function stageBudgetKey(
  status: StatusBudget | undefined,
  version: number
): "closingValues" | "negotiationValue" | "budgetValues" {
  if (status === "APPROVED") return "closingValues";
  if (version > 0) return "negotiationValue";
  return "budgetValues";
}

function sumServicePhaseTotals(
  services: ServiceData[],
  budgetKey: "closingValues" | "negotiationValue" | "budgetValues",
  version: number
): Totals {
  return services.reduce(
    (acc, service) => {
      const bag =
        budgetKey === "negotiationValue"
          ? service.negotiationValue?.[version - 1]
          : service[budgetKey];
      return {
        totalInBRL: acc.totalInBRL + (bag?.totals?.totalInBRL || 0),
        totalInCurrency:
          acc.totalInCurrency + (bag?.totals?.totalInCurrency || 0),
      };
    },
    { totalInBRL: 0, totalInCurrency: 0 }
  );
}

/** Selo de etapa/versão exibido ao lado do nome do orçamento na lista. */
const stagePillStyle: React.CSSProperties = {
  display: "inline-block",
  whiteSpace: "nowrap",
  padding: "5px",
  background: "#1361A4",
  color: "#FFF",
  borderRadius: "4px",
};

type Props = {
  /**
   * List row: full editor doc (`Budget`) or v2 summary (`BudgetSummary`).
   * Collapsed totals always use this row's snapshots (autosave, not keystroke).
   * Expanded categories read `budgetForRow` → `activeBudget` when hydrated.
   */
  budget: Budget | BudgetSummaryDto;
  index: number;
  status?: StatusBudget;
  version: number;
  val: string;
  onChangeDetailed: (identifier: string, version: number) => void;
  isLastNegotiation?: boolean;
};

const BudgetSummary: React.FC<Props> = ({
  budget,
  index,
  status,
  version,
  val,
  onChangeDetailed,
  isLastNegotiation,
}: Props) => {
  const { tabActive } = useProject();
  const { detailedBudget, activeBudget, isLoadingBudgetDetail } = useBudget();

  /** Workflow clones need a full document; prefer hydrated activeBudget over summary rows. */
  const workflowBudget: Budget = isFullBudget(budget) ? budget : activeBudget;

  const hasConsultingVersion = useMemo(() => {
    return budget.versionHistory.some((el) => el.isConsulting);
  }, [budget.versionHistory, budget]);

  const versionHasConsulting = useMemo(() => {
    return budget.versionHistory[version]?.isConsulting;
  }, [budget.versionHistory, version, budget]);

  const defineStatus = useCallback(() => {
    if (versionHasConsulting) return BudgetStatusEnum.CONSULTING;

    const statusKey = version
      ? budget.versionHistory[version].status
      : getBudgetCurrentStatus(budget.versionHistory);
    return BudgetStatusEnum[statusKey as keyof typeof BudgetStatusEnum];
  }, [versionHasConsulting, budget.versionHistory, version]);

  const defineTitle = useCallback(() => {
    const shouldDecrementIndex = hasConsultingBefore(
      budget.versionHistory,
      version
    );

    const displayIndex = shouldDecrementIndex ? version - 1 : version;

    return (
      <span>
        {budget.name || `Orçamento ${index + 1}`} &nbsp;
        {/*
          O "Total" desta linha NÃO é a mesma métrica em todas as abas
          (ver collapsed-totals.ts → getCollapsedBudgetTotal):
            Orçamento  → budgetValuesTotal
            Negociação → negociationValuesTotal[version - 1]
            Fechamento → closingValuesTotal
          Aprovar troca a aba sozinho (projectbudgets.context.tsx, action
          "APPROVED" → setTabActive(TabActive.APPROVED)). Sem o selo, a MESMA
          linha "Orçamento 1 / Total" exibia 119.336,18 na aba Orçamento e
          75.559,01 na aba Fechamento, e isso era lido como se o valor do
          orçamento tivesse sido sobrescrito pelo de um orçamento duplicado.
          Por isso o selo é obrigatório em TODAS as abas.
        */}
        {tabActive === TabActive.NEGOTIATION ? (
          <span style={stagePillStyle}>
            {defineStatus()} {versionHasConsulting ? "" : displayIndex}
          </span>
        ) : (
          <span style={stagePillStyle}>
            {tabActive === TabActive.APPROVED ? "Fechamento" : defineStatus()}
          </span>
        )}
      </span>
    );
  }, [
    budget.name,
    budget.versionHistory,
    index,
    version,
    versionHasConsulting,
    // `tabActive` decide qual selo é desenhado e é lido dentro do callback:
    // sem ele aqui a closure fica presa na aba anterior.
    tabActive,
    defineStatus,
  ]);

  const [isExpanded, setIsExpanded] = useState(false);
  const isThisRowSelected = detailedBudget === val;
  const isExpandedAndSelected = isThisRowSelected && isExpanded;

  const { triggerAction, filteredActions } = useBudgetSummaryActions({
    budget: workflowBudget,
    version,
    hasConsultingVersion,
    isLastNegotiation,
  });

  /** FE-P1-06: collapsed totals from list snapshots; active row tracks live calc. */
  const collapsedTotal = useMemo(() => {
    const source =
      isThisRowSelected && activeBudget?._id === budget._id
        ? budgetForRow(budget, activeBudget, featureFlags.useV2BudgetList)
        : budget;
    return getCollapsedBudgetTotal(source, status, version);
  }, [budget, status, version, isThisRowSelected, activeBudget]);
  const collapsedSavings = useMemo(() => {
    const source =
      isThisRowSelected && activeBudget?._id === budget._id
        ? budgetForRow(budget, activeBudget, featureFlags.useV2BudgetList)
        : budget;
    return getCollapsedBudgetSavings(source, status, version);
  }, [budget, status, version, isThisRowSelected, activeBudget]);

  /**
   * FE-P1-07: expanded categories from hydrated activeBudget (via budgetForRow).
   * List summary alone never drives category rows when v2 list is on.
   */
  const expandedBudget = useMemo(() => {
    if (!isExpandedAndSelected) return null;
    return budgetForRow(budget, activeBudget, featureFlags.useV2BudgetList);
  }, [isExpandedAndSelected, budget, activeBudget]);

  const hasHydratedServices = !!expandedBudget && isFullBudget(expandedBudget);

  const showDetailSpinner =
    isExpandedAndSelected &&
    (isLoadingBudgetDetail ||
      (featureFlags.useV2BudgetList && !hasHydratedServices));

  /** Status da etapa para os valores; `status` bruto segue só no selo. */
  const stageStatus = resolveSidebarStageStatus(status, version);

  const visibleStageServices = useMemo(() => {
    if (!expandedBudget || !isFullBudget(expandedBudget)) return [];
    return filterServicesCountedInStageTotals(
      expandedBudget.budgetServices,
      version,
      stageStatus,
      expandedBudget.supplierList,
      expandedBudget.addedItemTypeList
    );
  }, [expandedBudget, stageStatus, version]);

  /** Expanded footer uses the same counted services as the category rows. */
  const expandedFooterTotal = useMemo(() => {
    if (hasHydratedServices && expandedBudget) {
      return sumServicePhaseTotals(
        visibleStageServices,
        stageBudgetKey(stageStatus, version),
        version
      ).totalInBRL;
    }
    return collapsedTotal;
  }, [
    hasHydratedServices,
    expandedBudget,
    visibleStageServices,
    stageStatus,
    version,
    collapsedTotal,
  ]);

  const categoriesList = useMemo(() => {
    if (!isExpandedAndSelected || showDetailSpinner) return null;

    const source = expandedBudget;
    if (!source || !isFullBudget(source)) return null;

    const budgetKey = stageBudgetKey(stageStatus, version);
    return itemOptions.map((el: { value: BudgetItemType; label: string }) => {
      const servicesArr = visibleStageServices.filter(
        (item) => item.type === el.value
      );
      if (servicesArr.length < 1) return null;
      const totals = sumServicePhaseTotals(servicesArr, budgetKey, version);
      return (
        <BudgetItem
          key={el.value}
          budgetKey={budgetKey}
          selectable={false}
          type={el.value}
          servicesArr={servicesArr}
          stageStatus={stageStatus}
          total={totals}
          taxTotal={{
            totalInBRL: 0,
            totalInCurrency: 0,
          }}
          isSelected={false}
          version={version}
        />
      );
    });
  }, [
    expandedBudget,
    visibleStageServices,
    stageStatus,
    version,
    isExpandedAndSelected,
    showDetailSpinner,
  ]);

  const showSidebarActions = false;

  return (
    <Accordion
      onChange={(_, expanded) => {
        setIsExpanded(expanded);
        // FE-P1-07: expand/select share FE-P1-03 hydrate path (onChangeDetailed).
        if (expanded) {
          onChangeDetailed(val, version || 0);
        }
      }}
      onClick={() => onChangeDetailed(val, version || 0)}
      expanded={isExpandedAndSelected}
      sx={{
        border: "1px solid #eee",
        borderRadius: "4px",
        mb: 0.5,
        fontSize: "13px",
      }}
    >
      <AccordionSummary
        expandIcon={<BiChevronDown />}
        sx={{
          minHeight: 60,
          "&.Mui-expanded": {
            minHeight: 30,
            mt: 1,
          },
        }}
      >
        <Box display={"flex"} flexDirection={"column"} width={"100%"}>
          <RadioGroup.Item
            label={defineTitle()}
            value={val}
            name={!!version ? version.toString() : undefined}
          />
          {!isExpandedAndSelected && (
            <Box
              display={"flex"}
              justifyContent={"space-between"}
              width={"100%"}
            >
              <ItemTotalsSummary
                total={collapsedTotal}
                savings={collapsedSavings}
              />
            </Box>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails style={{ padding: 0 }}>
        {showDetailSpinner ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={2}
            minHeight={56}
          >
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {categoriesList}
            <div
              style={{
                padding: "16px 16px",
                borderTop: "1px solid rgba(0,0,0,0.1)",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                fontWeight: "bold",
              }}
            >
              <span>Total</span>
              <span>{formatCurrency(expandedFooterTotal)}</span>
            </div>
            <Box display={"flex"}>
              {showSidebarActions && (
                <Box ml={"auto"} p={1}>
                  <ActionSelect
                    label="Ações"
                    actionOptions={filteredActions}
                    triggerAction={triggerAction}
                    disabled={versionHasConsulting}
                  />
                </Box>
              )}
            </Box>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
export default BudgetSummary;
